import { useCallback, useEffect, useMemo, useState } from 'react';
import { rolesAPI } from '../services/api';
import { Pencil, Plus, Shield, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { getRoleDisplayName } from '../utils/helpers';
import { getPermissionCategoryLabel, getPermissionLabel } from '../utils/terminology';

const emptyRoleForm = {
  name: '',
  code: '',
  permission_codes: [],
};

const Roles = () => {
  const { hasPermission, entityCategory, terminology } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRoleForm);

  const loadRoles = useCallback(async () => {
    const [rolesRes, permissionsRes] = await Promise.all([
      rolesAPI.getAll(),
      rolesAPI.getPermissions(),
    ]);

    setRoles(rolesRes.data.roles);
    setPermissions(permissionsRes.data.permissions);
  }, []);

  useEffect(() => {
    loadRoles().catch(() => toast.error('Failed to load roles'));
  }, [loadRoles]);

  const permissionsByCategory = useMemo(() => {
    return permissions.reduce((acc, permission) => {
      const key = permission.category || 'general';
      acc[key] = acc[key] || [];
      acc[key].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const permissionsByCode = useMemo(() => new Map(
    permissions.map((permission) => [permission.code, permission])
  ), [permissions]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...form,
        code: editingId
          ? form.code
          : form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
      };
      if (editingId) {
        await rolesAPI.update(editingId, payload);
        toast.success('Role updated successfully');
      } else {
        await rolesAPI.create(payload);
        toast.success('Role created successfully');
      }
      setEditingId(null);
      setForm(emptyRoleForm);
      setShowForm(false);
      await loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (role) => {
    setEditingId(role.id);
    setForm({
      name: role.name || '',
      code: role.code || '',
      permission_codes: role.permission_codes || [],
    });
    setShowForm(true);
  };

  const handleRemove = async (role) => {
    try {
      await rolesAPI.remove(role.id);
      toast.success('Role deleted successfully');
      await loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete role');
    }
  };

  const togglePermission = (code) => {
    setForm((prev) => ({
      ...prev,
      permission_codes: prev.permission_codes.includes(code)
        ? prev.permission_codes.filter((item) => item !== code)
        : [...prev.permission_codes, code],
    }));
  };

  const canManage = hasPermission('roles.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles</h1>
          <p className="text-gray-500 text-sm mt-1">Role names follow the {terminology.categoryLabel.toLowerCase()} profile. Custom roles retain their own names.</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingId(null);
              setForm(emptyRoleForm);
              setShowForm((prev) => !prev);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : 'Add Role'}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Role Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={terminology.roleLabels.manager}
                required
              />
            </div>

          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Permissions</label>
            <div className="space-y-4 rounded-lg border border-gray-200 p-4">
              {Object.entries(permissionsByCategory).map(([category, items]) => (
                <div key={category}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{getPermissionCategoryLabel(category, terminology)}</p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((permission) => (
                      <label key={permission.code} className="flex items-start gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={form.permission_codes.includes(permission.code)}
                          onChange={() => togglePermission(permission.code)}
                          className="mt-0.5"
                        />
                        <div className="font-medium text-gray-800">{getPermissionLabel(permission, terminology)}</div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? 'Update Role' : 'Create Role'}
            </button>
            <button type="button" onClick={() => { setEditingId(null); setForm(emptyRoleForm); setShowForm(false); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {roles.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No roles found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Permissions</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {roles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <Shield size={16} className="text-blue-600" />
                      {getRoleDisplayName(role.code, entityCategory, role.name)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="flex flex-wrap gap-1">
                      {(role.permission_codes || []).length === 0 ? (
                        <span className="text-xs text-gray-400">No permissions</span>
                      ) : (
                        role.permission_codes.map((permissionCode) => (
                          <span key={permissionCode} className="rounded-full bg-gray-100 px-2 py-1 text-[11px] text-gray-600">
                            {getPermissionLabel(permissionsByCode.get(permissionCode) || { code: permissionCode }, terminology)}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{new Date(role.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => startEdit(role)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        <button onClick={() => handleRemove(role)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Roles;
